import html2canvas from 'html2canvas';
import React, { useCallback, useEffect, useRef, useState } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

type CardType   = 'stat' | 'screenshot';
type CardStyle  = 'bold-dark' | 'split-red' | 'deep-red' | 'deep-navy';
type CardFormat = 'square' | 'story' | 'landscape';

// ─── Constants ────────────────────────────────────────────────────────────────

const FORMATS: Record<CardFormat, { w: number; h: number; label: string; desc: string }> = {
  square:    { w: 540, h: 540,  label: 'Square 1:1',     desc: 'Instagram post' },
  story:     { w: 304, h: 540,  label: 'Story 9:16',     desc: 'Reels / TikTok' },
  landscape: { w: 720, h: 404,  label: 'Landscape 16:9', desc: 'LinkedIn' },
};

const STYLES: { id: CardStyle; label: string; desc: string }[] = [
  { id: 'bold-dark', label: 'Bold Dark',  desc: 'Dark + grid + glow' },
  { id: 'split-red', label: 'Split Red',  desc: 'Stat left, text right' },
  { id: 'deep-red',  label: 'Deep Red',   desc: '#150305 gradient' },
  { id: 'deep-navy', label: 'Deep Navy',  desc: '#011528 gradient' },
];

const BG_COLORS: Record<CardStyle, string> = {
  'bold-dark': '#0a0f1a',
  'split-red': '#0a0f1a',
  'deep-red':  '#150305',
  'deep-navy': '#011528',
};

const GRADIENT_OVERLAYS: Record<CardStyle, string> = {
  'bold-dark': 'none',
  'split-red': 'none',
  'deep-red':  'linear-gradient(135deg, rgba(191,30,46,0.35) 0%, rgba(21,3,5,0) 60%)',
  'deep-navy': 'linear-gradient(135deg, rgba(1,76,141,0.4) 0%, rgba(1,21,40,0) 60%)',
};

const COLOR_PRESETS = [
  { label: 'Red',   value: '#bf1e2e' },
  { label: 'Navy',  value: '#014c8d' },
  { label: 'White', value: '#ffffff' },
];

// ─── Sub-components ──────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 800, letterSpacing: 2,
      color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase',
      marginTop: 22, marginBottom: 9,
    }}>
      {children}
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontWeight: 600, marginBottom: 5 }}>{children}</div>;
}

function Input({
  value, onChange, placeholder, mono,
}: { value: string; onChange: (v: string) => void; placeholder?: string; mono?: boolean }) {
  return (
    <input
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: '100%', boxSizing: 'border-box',
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 8, padding: '9px 12px',
        color: '#fff', fontSize: mono ? 15 : 13,
        fontFamily: mono ? "'Barlow Condensed', sans-serif" : "'Barlow', sans-serif",
        fontWeight: mono ? 800 : 400,
        outline: 'none', marginBottom: 10,
      }}
    />
  );
}

function Slider({
  label, min, max, value, onChange, unit = 'px',
}: { label: string; min: number; max: number; value: number; onChange: (v: number) => void; unit?: string }) {
  return (
    <>
      <FieldLabel>{label} — {value}{unit}</FieldLabel>
      <input
        type="range" min={min} max={max} value={value}
        onChange={e => onChange(+e.target.value)}
        style={{ width: '100%', marginBottom: 14, accentColor: '#bf1e2e' }}
      />
    </>
  );
}

function UploadButton({
  uploaded, label, accept, onChange, onRemove,
}: {
  uploaded: boolean; label: string; accept: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
}) {
  return (
    <div style={{ marginBottom: 8 }}>
      <label style={{
        display: 'block', cursor: 'pointer',
        background: uploaded ? 'rgba(15,168,109,0.08)' : 'rgba(255,255,255,0.04)',
        border: `1px dashed ${uploaded ? 'rgba(15,168,109,0.4)' : 'rgba(255,255,255,0.14)'}`,
        borderRadius: 8, padding: '10px 14px',
        fontSize: 12, color: uploaded ? '#0fa86d' : 'rgba(255,255,255,0.45)',
        textAlign: 'center',
      }}>
        {uploaded ? `✓ ${label} — click to replace` : `+ Upload ${label}`}
        <input type="file" accept={accept} onChange={onChange} style={{ display: 'none' }} />
      </label>
      {uploaded && (
        <button onClick={onRemove} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', fontSize: 11, cursor: 'pointer', padding: '4px 0', display: 'block' }}>
          ✕ Remove
        </button>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function BrandPage() {

  // ── Card type ──
  const [cardType, setCardType] = useState<CardType>('stat');

  // ── Stat card state ──
  const [cardStyle, setCardStyle] = useState<CardStyle>('bold-dark');
  const [format,    setFormat]    = useState<CardFormat>('square');

  const [tagLabel,       setTagLabel]       = useState('ATHLETE DEVELOPMENT FACTS');
  const [statNumber,     setStatNumber]     = useState('4.8yr');
  const [mainText,       setMainText]       = useState('Average pro athlete career');
  const [supportingText, setSupportingText] = useState('Athletes deserve a plan for every stage. Build yours today.');
  const [sourceCredit,   setSourceCredit]   = useState('Source: BTHD Research 2026');

  const [statSize,       setStatSize]       = useState(88);
  const [mainSize,       setMainSize]       = useState(22);
  const [supportingSize, setSupportingSize] = useState(13);

  const [accentColor, setAccentColor] = useState('#bf1e2e');
  const [customColor, setCustomColor] = useState('#bf1e2e');
  const [useCustom,   setUseCustom]   = useState(false);
  const activeColor = useCustom ? customColor : accentColor;

  const [logoSrc,      setLogoSrc]      = useState<string | null>(null);
  const [logoX,        setLogoX]        = useState(20);
  const [logoY,        setLogoY]        = useState(20);
  const [logoSize,     setLogoSize]     = useState(72);
  const [logoSelected, setLogoSelected] = useState(false);

  const [heroSrc,     setHeroSrc]     = useState<string | null>(null);
  const [heroOpacity, setHeroOpacity] = useState(0.28);

  // ── Screenshot card state ──
  const [scrnFormat,      setScrnFormat]      = useState<CardFormat>('square');
  const [scrnLogoSrc,     setScrnLogoSrc]     = useState<string | null>(null);
  const [scrnScreenshot,  setScrnScreenshot]  = useState<string | null>(null);
  const [scrnHeadline1,   setScrnHeadline1]   = useState('Your Strength Profile,');
  const [scrnHeadline2,   setScrnHeadline2]   = useState('Mapped.');
  const [scrnSubcopy,     setScrnSubcopy]     = useState('Every athlete already has strengths built through sport. BTHD shows you where they translate.');
  const [scrnHeadlineSize, setScrnHeadlineSize] = useState(43);
  const [scrnSubcopySize,  setScrnSubcopySize]  = useState(11);
  const [scrnRotation,     setScrnRotation]     = useState(-5);
  const [scrnLogoSize,     setScrnLogoSize]     = useState(20);
  const [scrnScreenScale,  setScrnScreenScale]  = useState(92);
  // Drag positions (null = auto/centered)
  const [scrnLogoX,    setScrnLogoX]    = useState<number | null>(null);
  const [scrnLogoY,    setScrnLogoY]    = useState<number | null>(null);
  const [scrnImgX,     setScrnImgX]     = useState<number | null>(null);
  const [scrnImgY,     setScrnImgY]     = useState<number | null>(null);
  // Second screenshot
  const [scrnScreenshot2,   setScrnScreenshot2]   = useState<string | null>(null);
  const [scrnImg2Scale,     setScrnImg2Scale]     = useState(70);
  const [scrnImg2Rotation,  setScrnImg2Rotation]  = useState(5);
  const [scrnImg2X,         setScrnImg2X]         = useState<number | null>(null);
  const [scrnImg2Y,         setScrnImg2Y]         = useState<number | null>(null);

  // ── Download ──
  const [downloading, setDownloading] = useState(false);

  // ── Refs ──
  const cardRef       = useRef<HTMLDivElement>(null);
  const dragging      = useRef(false);
  const resizing      = useRef(false);
  const dragStart     = useRef({ x: 0, y: 0, lx: 0, ly: 0 });
  const resizeStart   = useRef({ x: 0, size: 0 });
  // Screenshot card drag refs
  const scrnDragTarget  = useRef<'logo' | 'img' | 'img2' | null>(null);
  const scrnDragStart   = useRef({ mx: 0, my: 0, ex: 0, ey: 0 });
  const scrnLogoRef     = useRef<HTMLDivElement>(null);
  const scrnImgRef      = useRef<HTMLDivElement>(null);
  const scrnImg2Ref     = useRef<HTMLDivElement>(null);

  const { w: cardW, h: cardH } = FORMATS[cardType === 'screenshot' ? scrnFormat : format];
  const cardBg = BG_COLORS[cardStyle];

  useEffect(() => {
    setLogoX(x => Math.max(0, Math.min(cardW - logoSize, x)));
    setLogoY(y => Math.max(0, Math.min(cardH - logoSize, y)));
  }, [cardW, cardH, logoSize]);

  const readFile = (file: File, cb: (s: string) => void) => {
    const r = new FileReader();
    r.onload = e => cb(e.target?.result as string);
    r.readAsDataURL(file);
  };

  const handleLogoUpload        = (e: React.ChangeEvent<HTMLInputElement>) => { const f = e.target.files?.[0]; if (f) readFile(f, setLogoSrc); };
  const handleHeroUpload        = (e: React.ChangeEvent<HTMLInputElement>) => { const f = e.target.files?.[0]; if (f) readFile(f, setHeroSrc); };
  const handleScrnLogoUpload    = (e: React.ChangeEvent<HTMLInputElement>) => { const f = e.target.files?.[0]; if (f) readFile(f, setScrnLogoSrc); };
  const handleScrnScreenUpload  = (e: React.ChangeEvent<HTMLInputElement>) => { const f = e.target.files?.[0]; if (f) { readFile(f, setScrnScreenshot); setScrnImgX(null); setScrnImgY(null); } };
  const handleScrnScreen2Upload = (e: React.ChangeEvent<HTMLInputElement>) => { const f = e.target.files?.[0]; if (f) { readFile(f, setScrnScreenshot2); setScrnImg2X(null); setScrnImg2Y(null); } };

  // Logo drag (stat card)
  const handleLogoDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault(); e.stopPropagation();
    dragging.current = true; setLogoSelected(true);
    const cx = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const cy = 'touches' in e ? e.touches[0].clientY : e.clientY;
    dragStart.current = { x: cx, y: cy, lx: logoX, ly: logoY };
  }, [logoX, logoY]);

  const handleResizeStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault(); e.stopPropagation();
    resizing.current = true;
    const cx = 'touches' in e ? e.touches[0].clientX : e.clientX;
    resizeStart.current = { x: cx, size: logoSize };
  }, [logoSize]);

  useEffect(() => {
    const onMove = (e: MouseEvent | TouchEvent) => {
      const cx = 'touches' in e ? (e as TouchEvent).touches[0].clientX : (e as MouseEvent).clientX;
      const cy = 'touches' in e ? (e as TouchEvent).touches[0].clientY : (e as MouseEvent).clientY;
      // Stat card logo drag
      if (dragging.current) {
        setLogoX(Math.max(0, Math.min(cardW - logoSize, dragStart.current.lx + (cx - dragStart.current.x))));
        setLogoY(Math.max(0, Math.min(cardH - logoSize, dragStart.current.ly + (cy - dragStart.current.y))));
      }
      if (resizing.current) {
        setLogoSize(Math.max(24, Math.min(200, resizeStart.current.size + (cx - resizeStart.current.x))));
      }
      // Screenshot card drags
      if (scrnDragTarget.current) {
        const dx = cx - scrnDragStart.current.mx;
        const dy = cy - scrnDragStart.current.my;
        const nx = scrnDragStart.current.ex + dx;
        const ny = scrnDragStart.current.ey + dy;
        if (scrnDragTarget.current === 'logo')  { setScrnLogoX(nx); setScrnLogoY(ny); }
        if (scrnDragTarget.current === 'img')   { setScrnImgX(nx);  setScrnImgY(ny); }
        if (scrnDragTarget.current === 'img2')  { setScrnImg2X(nx); setScrnImg2Y(ny); }
      }
    };
    const onUp = () => { dragging.current = false; resizing.current = false; scrnDragTarget.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend',  onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend',  onUp);
    };
  }, [cardW, cardH, logoSize]);

  // Screenshot card — drag start helpers
  const startScrnDrag = useCallback((
    target: 'logo' | 'img' | 'img2',
    e: React.MouseEvent | React.TouchEvent,
    elRef: React.RefObject<HTMLDivElement | null>,
    currentX: number | null,
    currentY: number | null,
  ) => {
    e.preventDefault(); e.stopPropagation();
    const cx = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const cy = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const cardRect = cardRef.current?.getBoundingClientRect();
    const elRect   = elRef.current?.getBoundingClientRect();
    if (!cardRect || !elRect) return;
    const ex = currentX ?? (elRect.left - cardRect.left);
    const ey = currentY ?? (elRect.top  - cardRect.top);
    scrnDragTarget.current = target;
    scrnDragStart.current  = { mx: cx, my: cy, ex, ey };
    // Lock in explicit position on first drag
    if (target === 'logo')  { setScrnLogoX(ex); setScrnLogoY(ey); }
    if (target === 'img')   { setScrnImgX(ex);  setScrnImgY(ey); }
    if (target === 'img2')  { setScrnImg2X(ex); setScrnImg2Y(ey); }
  }, []);

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    setLogoSelected(false);
    await new Promise(r => setTimeout(r, 80));
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 4, useCORS: true, allowTaint: true,
        backgroundColor: null, logging: false,
      });
      const a = document.createElement('a');
      a.download = `bthd-card-${Date.now()}.png`;
      a.href = canvas.toDataURL('image/png');
      a.click();
    } catch (err) {
      console.error('Download failed:', err);
    }
    setDownloading(false);
  };

  const splitVertical = format === 'story';

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div
      style={{ minHeight: '100vh', background: '#060a14', fontFamily: "'Barlow', sans-serif", color: '#fff' }}
      onClick={() => setLogoSelected(false)}
    >
      {/* ── Page header ── */}
      <div style={{
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        padding: '13px 24px', display: 'flex', alignItems: 'center', gap: 14,
        background: '#0a0f1a', position: 'sticky', top: 0, zIndex: 20,
      }}>
        <div style={{ width: 28, height: 28, background: '#bf1e2e', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Barlow Condensed', sans-serif", fontSize: 13, fontWeight: 900 }}>B</div>
        <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, fontSize: 16, letterSpacing: 1 }}>BTHD</span>
        <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 15 }}>/</span>
        <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>Brand Card Generator</span>
        <div style={{ marginLeft: 'auto', fontSize: 11, color: 'rgba(255,255,255,0.25)' }}>bthd.app/brand</div>
      </div>

      {/* ── Layout ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', minHeight: 'calc(100vh - 54px)' }}>

        {/* ════ Controls ════ */}
        <div style={{
          width: 310, minWidth: 280, flexShrink: 0,
          background: '#0a0f1a', padding: '18px 18px 48px',
          borderRight: '1px solid rgba(255,255,255,0.06)',
          overflowY: 'auto', maxHeight: 'calc(100vh - 54px)',
          boxSizing: 'border-box',
        }}>

          {/* ── Card Type selector ── */}
          <SectionLabel>Card Type</SectionLabel>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 4 }}>
            {([
              { id: 'stat',       label: 'Stat Card',          desc: 'Stats & data' },
              { id: 'screenshot', label: 'Screenshot / In-App', desc: 'App mockup post' },
            ] as { id: CardType; label: string; desc: string }[]).map(ct => (
              <button key={ct.id} onClick={() => setCardType(ct.id)} style={{
                background: cardType === ct.id ? 'rgba(191,30,46,0.18)' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${cardType === ct.id ? '#bf1e2e' : 'rgba(255,255,255,0.09)'}`,
                borderRadius: 8, padding: '8px 10px', cursor: 'pointer', textAlign: 'left',
                color: cardType === ct.id ? '#fff' : 'rgba(255,255,255,0.5)',
              }}>
                <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 12, fontWeight: 800 }}>{ct.label}</div>
                <div style={{ fontSize: 10, marginTop: 2, opacity: 0.55 }}>{ct.desc}</div>
              </button>
            ))}
          </div>

          {/* ════ STAT CARD CONTROLS ════ */}
          {cardType === 'stat' && (
            <>
              <SectionLabel>Card Style</SectionLabel>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {STYLES.map(s => (
                  <button key={s.id} onClick={() => setCardStyle(s.id)} style={{
                    background: cardStyle === s.id ? 'rgba(191,30,46,0.18)' : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${cardStyle === s.id ? '#bf1e2e' : 'rgba(255,255,255,0.09)'}`,
                    borderRadius: 8, padding: '8px 10px', cursor: 'pointer', textAlign: 'left',
                    color: cardStyle === s.id ? '#fff' : 'rgba(255,255,255,0.5)',
                  }}>
                    <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 13, fontWeight: 800 }}>{s.label}</div>
                    <div style={{ fontSize: 10, marginTop: 2, opacity: 0.55 }}>{s.desc}</div>
                  </button>
                ))}
              </div>

              <SectionLabel>Format</SectionLabel>
              <div style={{ display: 'flex', gap: 7 }}>
                {(Object.entries(FORMATS) as [CardFormat, typeof FORMATS[CardFormat]][]).map(([key, val]) => (
                  <button key={key} onClick={() => setFormat(key)} style={{
                    flex: 1,
                    background: format === key ? 'rgba(191,30,46,0.18)' : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${format === key ? '#bf1e2e' : 'rgba(255,255,255,0.09)'}`,
                    borderRadius: 8, padding: '8px 4px', cursor: 'pointer',
                    color: format === key ? '#fff' : 'rgba(255,255,255,0.5)',
                  }}>
                    <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 11, fontWeight: 800 }}>{val.label}</div>
                    <div style={{ fontSize: 10, marginTop: 1, opacity: 0.5 }}>{val.desc}</div>
                  </button>
                ))}
              </div>

              <SectionLabel>Content</SectionLabel>
              <FieldLabel>Tag Label</FieldLabel>
              <Input value={tagLabel} onChange={setTagLabel} placeholder="e.g. ATHLETE CAREER FACTS" />
              <FieldLabel>Stat / Big Number</FieldLabel>
              <Input value={statNumber} onChange={setStatNumber} placeholder="e.g. 4.8yr" mono />
              <FieldLabel>Main Text</FieldLabel>
              <Input value={mainText} onChange={setMainText} placeholder="e.g. Average pro athlete career" />
              <FieldLabel>Supporting Text</FieldLabel>
              <Input value={supportingText} onChange={setSupportingText} placeholder="Additional context..." />
              <FieldLabel>Source Credit</FieldLabel>
              <Input value={sourceCredit} onChange={setSourceCredit} placeholder="Source: ..." />

              <SectionLabel>Font Sizes</SectionLabel>
              <Slider label="Stat Number"     min={40} max={220} value={statSize}       onChange={setStatSize} />
              <Slider label="Main Text"       min={11} max={48}  value={mainSize}       onChange={setMainSize} />
              <Slider label="Supporting Text" min={9}  max={28}  value={supportingSize} onChange={setSupportingSize} />

              <SectionLabel>Accent Color</SectionLabel>
              <div style={{ display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap' }}>
                {COLOR_PRESETS.map(c => (
                  <button key={c.value} title={c.label} onClick={() => { setAccentColor(c.value); setUseCustom(false); }} style={{
                    width: 30, height: 30, borderRadius: '50%', background: c.value,
                    border: (!useCustom && accentColor === c.value) ? '3px solid #fff' : '2px solid rgba(255,255,255,0.2)',
                    cursor: 'pointer', boxSizing: 'border-box', outline: 'none',
                  }} />
                ))}
                <input type="color" value={customColor}
                  onChange={e => { setCustomColor(e.target.value); setUseCustom(true); }}
                  title="Custom color"
                  style={{ width: 30, height: 30, borderRadius: '50%', padding: 0, boxSizing: 'border-box', border: useCustom ? '3px solid #fff' : '2px solid rgba(255,255,255,0.2)', cursor: 'pointer', background: 'none', outline: 'none' }}
                />
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>Custom</span>
              </div>

              <SectionLabel>Logo</SectionLabel>
              <UploadButton uploaded={!!logoSrc} label="Logo (PNG/SVG)" accept="image/*" onChange={handleLogoUpload} onRemove={() => setLogoSrc(null)} />
              {logoSrc && (
                <>
                  <Slider label="Logo Size" min={24} max={200} value={logoSize} onChange={setLogoSize} />
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginBottom: 4, lineHeight: 1.5 }}>
                    Drag logo on card. Corner dot to resize. Click away to deselect.
                  </div>
                </>
              )}

              <SectionLabel>Hero Image</SectionLabel>
              <UploadButton uploaded={!!heroSrc} label="Athlete Photo" accept="image/*" onChange={handleHeroUpload} onRemove={() => setHeroSrc(null)} />
              {heroSrc && (
                <Slider label="Opacity" min={5} max={55} value={Math.round(heroOpacity * 100)} onChange={v => setHeroOpacity(v / 100)} unit="%" />
              )}
            </>
          )}

          {/* ════ SCREENSHOT CARD CONTROLS ════ */}
          {cardType === 'screenshot' && (
            <>
              <SectionLabel>Format</SectionLabel>
              <div style={{ display: 'flex', gap: 7 }}>
                {(Object.entries(FORMATS) as [CardFormat, typeof FORMATS[CardFormat]][]).map(([key, val]) => (
                  <button key={key} onClick={() => setScrnFormat(key)} style={{
                    flex: 1,
                    background: scrnFormat === key ? 'rgba(191,30,46,0.18)' : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${scrnFormat === key ? '#bf1e2e' : 'rgba(255,255,255,0.09)'}`,
                    borderRadius: 8, padding: '8px 4px', cursor: 'pointer',
                    color: scrnFormat === key ? '#fff' : 'rgba(255,255,255,0.5)',
                  }}>
                    <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 11, fontWeight: 800 }}>{val.label}</div>
                    <div style={{ fontSize: 10, marginTop: 1, opacity: 0.5 }}>{val.desc}</div>
                  </button>
                ))}
              </div>

              <SectionLabel>Assets</SectionLabel>
              <FieldLabel>BTHD Logo</FieldLabel>
              <UploadButton uploaded={!!scrnLogoSrc} label="Logo (PNG/SVG)" accept="image/*" onChange={handleScrnLogoUpload} onRemove={() => { setScrnLogoSrc(null); setScrnLogoX(null); setScrnLogoY(null); }} />
              <FieldLabel>App Screenshot 1</FieldLabel>
              <UploadButton uploaded={!!scrnScreenshot} label="App Screenshot" accept="image/*" onChange={handleScrnScreenUpload} onRemove={() => { setScrnScreenshot(null); setScrnImgX(null); setScrnImgY(null); }} />
              <FieldLabel>App Screenshot 2 (optional)</FieldLabel>
              <UploadButton uploaded={!!scrnScreenshot2} label="Second Screenshot" accept="image/*" onChange={handleScrnScreen2Upload} onRemove={() => { setScrnScreenshot2(null); setScrnImg2X(null); setScrnImg2Y(null); }} />
              {scrnScreenshot2 && (
                <>
                  <Slider label="Screenshot 2 Size"  min={30} max={130} value={scrnImg2Scale}    onChange={setScrnImg2Scale}    unit="%" />
                  <Slider label="Screenshot 2 Tilt"  min={-15} max={15} value={scrnImg2Rotation} onChange={setScrnImg2Rotation} unit="°" />
                </>
              )}
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', lineHeight: 1.5, marginBottom: 4 }}>
                Drag logo, screenshots, and copy on the card to reposition freely. Use sliders to resize.
              </div>

              <SectionLabel>Copy</SectionLabel>
              <FieldLabel>Headline — white (press Enter for new line not needed)</FieldLabel>
              <Input value={scrnHeadline1} onChange={setScrnHeadline1} placeholder="e.g. Your Strength Profile," />
              <FieldLabel>Key Word — red</FieldLabel>
              <Input value={scrnHeadline2} onChange={setScrnHeadline2} placeholder="e.g. Mapped." />
              <FieldLabel>Subcopy</FieldLabel>
              <Input value={scrnSubcopy} onChange={setScrnSubcopy} placeholder="Supporting description..." />

              <SectionLabel>Sizing</SectionLabel>
              <Slider label="Logo Size"       min={14} max={60}  value={scrnLogoSize}     onChange={setScrnLogoSize} />
              <Slider label="Screenshot Size" min={40} max={130} value={scrnScreenScale}   onChange={setScrnScreenScale} unit="%" />
              <Slider label="Headline Size"   min={28} max={68}  value={scrnHeadlineSize}  onChange={setScrnHeadlineSize} />
              <Slider label="Subcopy Size"    min={8}  max={18}  value={scrnSubcopySize}   onChange={setScrnSubcopySize} />
              <Slider label="Screenshot Tilt" min={-15} max={15} value={scrnRotation}      onChange={setScrnRotation} unit="°" />
            </>
          )}

          {/* ── Download (shared) ── */}
          <div style={{ marginTop: 28 }}>
            <button onClick={handleDownload} disabled={downloading} style={{
              width: '100%', background: downloading ? '#222' : '#bf1e2e',
              color: '#fff', border: 'none', borderRadius: 10, padding: '15px 0',
              fontFamily: "'Barlow Condensed', sans-serif",
              fontSize: 15, fontWeight: 900, letterSpacing: 1,
              cursor: downloading ? 'not-allowed' : 'pointer', transition: 'background 0.2s',
            }}>
              {downloading ? 'Generating…' : '↓ Download PNG'}
            </button>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.2)', textAlign: 'center', marginTop: 8 }}>
              Exports at 4× · {cardW * 4} × {cardH * 4}px
            </div>
          </div>
        </div>

        {/* ════ Preview ════ */}
        <div style={{
          flex: 1, minWidth: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'flex-start', padding: '36px 20px 60px',
          background: '#0d1120', overflowX: 'auto',
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: 'rgba(255,255,255,0.2)', marginBottom: 24, fontFamily: "'Barlow Condensed', sans-serif" }}>
            LIVE PREVIEW
          </div>

          {/* ════ STAT CARD ════ */}
          {cardType === 'stat' && (
            <div
              ref={cardRef}
              onClick={e => e.stopPropagation()}
              style={{
                width: cardW, height: cardH,
                position: 'relative', overflow: 'hidden',
                flexShrink: 0, borderRadius: 4,
                background: cardBg,
                boxShadow: cardStyle === 'bold-dark'
                  ? `0 0 0 1px rgba(255,255,255,0.06), 0 24px 64px rgba(0,0,0,0.8), inset 0 0 80px rgba(191,30,46,0.07)`
                  : '0 24px 64px rgba(0,0,0,0.7)',
              }}
            >
              {cardStyle === 'bold-dark' && (
                <div style={{
                  position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1,
                  backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
                  backgroundSize: '40px 40px',
                }} />
              )}
              {GRADIENT_OVERLAYS[cardStyle] !== 'none' && (
                <div style={{ position: 'absolute', inset: 0, background: GRADIENT_OVERLAYS[cardStyle], pointerEvents: 'none', zIndex: 1 }} />
              )}
              {cardStyle === 'split-red' && (
                splitVertical
                  ? <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '45%', background: activeColor, zIndex: 0 }} />
                  : <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: '45%', background: activeColor, zIndex: 0 }} />
              )}
              {heroSrc && (
                <>
                  <img src={heroSrc} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: heroOpacity, pointerEvents: 'none', zIndex: 2 }} draggable={false} />
                  <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 3, background: `linear-gradient(to bottom, transparent 25%, ${cardBg}cc 65%, ${cardBg} 100%)` }} />
                </>
              )}

              {cardStyle === 'split-red' ? (
                splitVertical ? (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', zIndex: 4 }}>
                    <div style={{ height: '45%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '12px 20px 8px' }}>
                      {tagLabel && <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 9, fontWeight: 800, color: 'rgba(255,255,255,0.75)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>{tagLabel}</div>}
                      <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: Math.round(statSize * 0.75), fontWeight: 900, color: '#fff', lineHeight: 1, textAlign: 'center' }}>{statNumber}</div>
                    </div>
                    <div style={{ flex: 1, padding: '16px 22px 38px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      {mainText && <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: Math.round(mainSize * 0.95), fontWeight: 700, color: '#fff', lineHeight: 1.25, marginBottom: 7 }}>{mainText}</div>}
                      {supportingText && <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: supportingSize, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>{supportingText}</div>}
                    </div>
                  </div>
                ) : (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', zIndex: 4 }}>
                    <div style={{ width: '45%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px 16px' }}>
                      {tagLabel && <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 9, fontWeight: 800, color: 'rgba(255,255,255,0.7)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6, textAlign: 'center' }}>{tagLabel}</div>}
                      <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: Math.round(statSize * 0.72), fontWeight: 900, color: '#fff', lineHeight: 1, textAlign: 'center' }}>{statNumber}</div>
                    </div>
                    <div style={{ flex: 1, padding: '24px 20px 38px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      {mainText && <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: Math.round(mainSize * 0.95), fontWeight: 700, color: '#fff', lineHeight: 1.25, marginBottom: 8 }}>{mainText}</div>}
                      {supportingText && <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: supportingSize, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>{supportingText}</div>}
                    </div>
                  </div>
                )
              ) : (
                <div style={{ position: 'absolute', zIndex: 4, top: 22, left: 28, right: 28, bottom: 36, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  {tagLabel && <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 10, fontWeight: 800, letterSpacing: 2.5, textTransform: 'uppercase', marginBottom: 12, color: activeColor }}>{tagLabel}</div>}
                  <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: statSize, fontWeight: 900, color: cardStyle === 'bold-dark' ? activeColor : '#ffffff', lineHeight: 0.92, letterSpacing: -2, marginBottom: 12 }}>{statNumber}</div>
                  {mainText && <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: mainSize, fontWeight: 700, color: '#ffffff', lineHeight: 1.25, marginBottom: 8 }}>{mainText}</div>}
                  {supportingText && <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: supportingSize, color: 'rgba(255,255,255,0.6)', lineHeight: 1.55 }}>{supportingText}</div>}
                </div>
              )}

              {logoSrc && cardStyle !== 'split-red' && (
                <div
                  style={{ position: 'absolute', zIndex: 6, left: logoX, top: logoY, width: logoSize, cursor: dragging.current ? 'grabbing' : 'grab', userSelect: 'none', touchAction: 'none' }}
                  onMouseDown={handleLogoDragStart} onTouchStart={handleLogoDragStart}
                  onClick={e => { e.stopPropagation(); setLogoSelected(true); }}
                >
                  <img src={logoSrc} alt="" style={{ width: '100%', display: 'block', pointerEvents: 'none' }} draggable={false} />
                  {logoSelected && (
                    <div
                      style={{ position: 'absolute', bottom: -5, right: -5, width: 12, height: 12, background: activeColor, border: '1.5px solid rgba(255,255,255,0.7)', borderRadius: 3, cursor: 'se-resize' }}
                      onMouseDown={handleResizeStart} onTouchStart={handleResizeStart}
                    />
                  )}
                </div>
              )}
              {logoSrc && cardStyle === 'split-red' && (
                <div style={{ position: 'absolute', zIndex: 6, ...(splitVertical ? { top: '5%', left: '50%', transform: 'translateX(-50%)', width: Math.min(logoSize, 80) } : { left: '5%', top: '50%', transform: 'translateY(-50%)', width: Math.min(logoSize, 80) }) }}>
                  <img src={logoSrc} alt="" style={{ width: '100%', display: 'block' }} draggable={false} />
                </div>
              )}

              {sourceCredit && (
                <div style={{ position: 'absolute', zIndex: 5, bottom: 26, right: 14, fontFamily: "'Barlow', sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>
                  {sourceCredit}
                </div>
              )}
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 7, padding: '6px 14px', background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontFamily: "'Barlow', sans-serif", fontSize: Math.max(8, Math.round(supportingSize * 0.7)), color: 'rgba(255,255,255,0.5)', fontWeight: 600, letterSpacing: 0.2 }}>Bigger Than Hoop Dreams · bthd.app</span>
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: activeColor, flexShrink: 0 }} />
              </div>
            </div>
          )}

          {/* ════ SCREENSHOT / IN-APP CARD ════ */}
          {cardType === 'screenshot' && (() => {
            const sw = FORMATS[scrnFormat].w;
            const sh = FORMATS[scrnFormat].h;
            const pad = Math.round(sw * 0.052);
            const logoH = scrnLogoSize;
            const tagSize = Math.round(sw * 0.017);
            const copyZoneH = scrnHeadlineSize * 2 * 0.97 + 10 + scrnSubcopySize * 2 * 1.5 + pad;
            const screenshotZoneH = sh - (pad * 2 + logoH + 8) - copyZoneH;
            const screenshotTop = pad + logoH + 8 + Math.round(screenshotZoneH * 0.05);
            const baseImgH = Math.round(screenshotZoneH * 0.92);
            const maxImgH = Math.round(baseImgH * scrnScreenScale / 100);
            const maxImgW = Math.round(sw * 0.85 * scrnScreenScale / 100);

            return (
              <div
                ref={cardRef}
                onClick={e => e.stopPropagation()}
                style={{
                  width: sw, height: sh,
                  position: 'relative', overflow: 'hidden',
                  flexShrink: 0, borderRadius: 4,
                  background: '#080d16',
                  boxShadow: '0 24px 64px rgba(0,0,0,0.85)',
                }}
              >
                {/* Ambient glow */}
                <div style={{
                  position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
                  background: `
                    radial-gradient(ellipse 80% 55% at 50% 0%, rgba(1,76,141,0.28) 0%, transparent 60%),
                    radial-gradient(ellipse 60% 40% at 50% 100%, rgba(191,30,46,0.10) 0%, transparent 65%)
                  `,
                }} />

                {/* "INSIDE THE APP" tag — top right, fixed */}
                <div style={{
                  position: 'absolute', top: pad, right: pad,
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: 700, fontSize: tagSize,
                  letterSpacing: Math.round(tagSize * 0.28),
                  textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.42)',
                  whiteSpace: 'nowrap',
                  zIndex: 4, pointerEvents: 'none',
                }}>
                  Inside the App
                </div>

                {/* Logo — draggable, top-left by default */}
                <div
                  ref={scrnLogoRef}
                  onMouseDown={e => startScrnDrag('logo', e, scrnLogoRef, scrnLogoX, scrnLogoY)}
                  onTouchStart={e => startScrnDrag('logo', e, scrnLogoRef, scrnLogoX, scrnLogoY)}
                  style={{
                    position: 'absolute',
                    left: scrnLogoX ?? pad,
                    top:  scrnLogoY ?? pad,
                    zIndex: 5, cursor: 'grab', userSelect: 'none', touchAction: 'none',
                  }}
                >
                  {scrnLogoSrc
                    ? <img src={scrnLogoSrc} alt="BTHD" style={{ height: logoH, width: 'auto', display: 'block', pointerEvents: 'none' }} />
                    : <div style={{ height: logoH, display: 'flex', alignItems: 'center', gap: 5 }}>
                        <div style={{ width: logoH, height: logoH, background: '#bf1e2e', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Barlow Condensed', sans-serif", fontSize: Math.round(logoH * 0.65), fontWeight: 900, color: '#fff', flexShrink: 0 }}>B</div>
                        <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, fontSize: Math.round(logoH * 0.7), color: '#fff', letterSpacing: 0.5 }}>BTHD</span>
                      </div>
                  }
                </div>

                {/* Screenshot 1 — draggable */}
                <div
                  ref={scrnImgRef}
                  onMouseDown={e => startScrnDrag('img', e, scrnImgRef, scrnImgX, scrnImgY)}
                  onTouchStart={e => startScrnDrag('img', e, scrnImgRef, scrnImgX, scrnImgY)}
                  style={{
                    position: 'absolute',
                    ...(scrnImgX !== null && scrnImgY !== null
                      ? { left: scrnImgX, top: scrnImgY, transform: `rotate(${scrnRotation}deg)` }
                      : { top: screenshotTop, left: '50%', transform: `translateX(-50%) rotate(${scrnRotation}deg)` }
                    ),
                    zIndex: 2, cursor: 'grab', userSelect: 'none', touchAction: 'none',
                  }}
                >
                  {scrnScreenshot ? (
                    <img
                      src={scrnScreenshot}
                      alt=""
                      style={{
                        display: 'block',
                        maxHeight: maxImgH,
                        maxWidth: maxImgW,
                        objectFit: 'contain',
                        pointerEvents: 'none',
                        filter: `drop-shadow(0 ${Math.round(sh * 0.026)}px ${Math.round(sh * 0.052)}px rgba(0,0,0,0.9))`,
                      }}
                    />
                  ) : (
                    <div style={{
                      width: Math.round(sw * 0.52), height: Math.round(maxImgH * 0.85),
                      border: '2px dashed rgba(255,255,255,0.1)',
                      borderRadius: Math.round(sw * 0.02),
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      color: 'rgba(255,255,255,0.18)', fontSize: Math.round(sw * 0.022),
                      fontFamily: "'Barlow', sans-serif", textAlign: 'center', padding: 16,
                    }}>
                      <div style={{ fontSize: Math.round(sw * 0.055), marginBottom: 8, opacity: 0.4 }}>⬡</div>
                      Upload app screenshot
                    </div>
                  )}
                </div>

                {/* Screenshot 2 — draggable, optional */}
                {scrnScreenshot2 && (() => {
                  const img2MaxH = Math.round(baseImgH * scrnImg2Scale / 100);
                  const img2MaxW = Math.round(sw * 0.85 * scrnImg2Scale / 100);
                  const img2DefaultTop  = Math.round(sh * 0.12);
                  const img2DefaultLeft = Math.round(sw * 0.55);
                  return (
                    <div
                      ref={scrnImg2Ref}
                      onMouseDown={e => startScrnDrag('img2', e, scrnImg2Ref, scrnImg2X, scrnImg2Y)}
                      onTouchStart={e => startScrnDrag('img2', e, scrnImg2Ref, scrnImg2X, scrnImg2Y)}
                      style={{
                        position: 'absolute',
                        ...(scrnImg2X !== null && scrnImg2Y !== null
                          ? { left: scrnImg2X, top: scrnImg2Y, transform: `rotate(${scrnImg2Rotation}deg)` }
                          : { top: img2DefaultTop, left: img2DefaultLeft, transform: `rotate(${scrnImg2Rotation}deg)` }
                        ),
                        zIndex: 2, cursor: 'grab', userSelect: 'none', touchAction: 'none',
                      }}
                    >
                      <img
                        src={scrnScreenshot2}
                        alt=""
                        style={{
                          display: 'block',
                          maxHeight: img2MaxH,
                          maxWidth: img2MaxW,
                          objectFit: 'contain',
                          pointerEvents: 'none',
                          filter: `drop-shadow(0 ${Math.round(sh * 0.026)}px ${Math.round(sh * 0.052)}px rgba(0,0,0,0.9))`,
                        }}
                      />
                    </div>
                  );
                })()}

                {/* Copy: headline + subcopy — anchored bottom-left */}
                <div style={{
                  position: 'absolute',
                  bottom: pad,
                  left: pad,
                  right: pad,
                  zIndex: 3,
                }}>
                  <div style={{
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontWeight: 900,
                    fontSize: scrnHeadlineSize,
                    lineHeight: 0.97,
                    letterSpacing: -0.5,
                    textTransform: 'uppercase',
                    color: '#ffffff',
                  }}>
                    {scrnHeadline1}
                  </div>
                  <div style={{
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontWeight: 900,
                    fontSize: scrnHeadlineSize,
                    lineHeight: 0.97,
                    letterSpacing: -0.5,
                    textTransform: 'uppercase',
                    color: '#bf1e2e',
                  }}>
                    {scrnHeadline2}
                  </div>
                  <div style={{
                    marginTop: Math.round(scrnHeadlineSize * 0.24),
                    fontFamily: "'Barlow', sans-serif",
                    fontWeight: 400,
                    fontSize: scrnSubcopySize,
                    lineHeight: 1.5,
                    color: 'rgba(255,255,255,0.60)',
                  }}>
                    {scrnSubcopy}
                  </div>
                </div>

              </div>
            );
          })()}

          <div style={{ marginTop: 14, fontSize: 11, color: 'rgba(255,255,255,0.2)', textAlign: 'center', fontFamily: "'Barlow', sans-serif" }}>
            {FORMATS[cardType === 'screenshot' ? scrnFormat : format].label} · {cardW}×{cardH} preview · {cardW * 4}×{cardH * 4} download
          </div>

          {/* Tips */}
          <div style={{
            marginTop: 32, maxWidth: 520, width: '100%',
            background: 'rgba(255,255,255,0.025)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 12, padding: '16px 20px',
          }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 2, color: 'rgba(255,255,255,0.25)', marginBottom: 10, fontFamily: "'Barlow Condensed', sans-serif" }}>TIPS</div>
            {(cardType === 'stat' ? [
              'Drag the logo to reposition it on the card',
              'Click logo once to show resize handle, then drag corner to resize',
              'Hero images add depth — keep opacity between 20–40% for readable text',
              'Bold Dark style has a subtle grid pattern and red ambient glow',
              'Split Red puts the stat number in the accent panel automatically',
              'Source credit appears small in the bottom right corner',
            ] : [
              'Upload the BTHD logo first — use the colored PNG for dark backgrounds',
              'Upload your iPad mockup screenshot (already includes the device frame)',
              'Use Headline for the white line, Key Word for the red line below it',
              'Tilt slider adjusts the screenshot rotation — default is -5° like the reference',
              'Headline size and subcopy size adjust live in the preview',
              'Download exports at 2× resolution (1080×1080 for square)',
            ]).map((tip, i) => (
              <div key={i} style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginBottom: 6, paddingLeft: 14, position: 'relative', fontFamily: "'Barlow', sans-serif" }}>
                <span style={{ position: 'absolute', left: 0, color: '#bf1e2e', fontWeight: 900 }}>·</span>
                {tip}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
